import { useEffect, useRef } from 'react'

/** The lifetime the server granted one board's live subscriptions, as this tab learned it. */
export interface LiveSearchLifetime {
    /** When the subscription stops matching (ms since epoch) — the EARLIEST across that board's lanes. */
    expiresAt: number
    /** When this tab was told, so the lifetime's length is known without hardcoding the server's TTL. */
    receivedAt: number
}

/**
 * How much of the granted lifetime is deliberately left unused.
 *
 * The renewal is the board's ordinary search, and the server only extends `expires_at` when LESS THAN HALF
 * the lifetime remains (see `createLiveSearchSubscription`'s half-life rule) — a search run earlier than that
 * reuses the subscription without moving its expiry, so renewing too eagerly renews nothing. Firing with 40%
 * left means 10% of the lifetime past the half-way point: comfortably inside the window that extends, and
 * still 12 minutes of slack on the default 30-minute TTL for a slow search or a sleeping laptop.
 */
const RENEWAL_LEAD_FRACTION = 0.4

/**
 * Keep a board's live subscriptions alive for as long as its tab is open.
 *
 * A subscription expires `LIVE_SEARCH_TTL_SEC` (30 minutes by default) after the search that created it, and
 * nothing on this board renewed it: there is no auto-refresh timer here, `refresh()` has only a manual button
 * and the reconnect repair behind it, and switching back to a cached tab restores its snapshot without
 * searching. So a board left open simply stopped receiving live deliveries and cancellations half an hour
 * later, with nothing on screen to say so.
 *
 * The renewal is the board's own quiet re-fetch — the SAME call Auto Refresh and the reconnect repair make.
 * It sends no `intent`, which is the path that extends `expires_at` and re-indexes Redis, so no new endpoint
 * is needed and the board's data is refreshed in the same breath.
 *
 * Fires immediately when the lifetime is ALREADY past, which is what covers switching back to a tab whose
 * subscription lapsed while it was off screen: the tab becomes active, this sees its stale lifetime, and the
 * re-search both restores a current board and registers a live subscription again.
 *
 * Deferred while the tab is hidden — a background tab is nobody's screen, and renewing it would pay for a
 * search (plus a pooled DAT token) nobody is looking at. It renews when it is looked at, which is also when
 * live delivery starts to matter again.
 */
export const useLiveSearchRenewal = (lifetime: LiveSearchLifetime | undefined, renew: () => void, enabled: boolean): void => {
    // Read through a ref so a new `renew` identity on every render cannot re-arm the timer.
    const renewRef = useRef(renew)
    renewRef.current = renew
    /** The lifetime this hook has already acted on, so one that stays stale cannot renew in a loop. */
    const firedForRef = useRef<string | null>(null)

    useEffect(() => {
        if (!enabled || !lifetime) return
        const granted = lifetime.expiresAt - lifetime.receivedAt
        if (granted <= 0) return
        // A failed renewal leaves the SAME lifetime in place and therefore still overdue. Without this the
        // effect would re-fire the instant it re-ran, and a search erroring for any reason (an expired token,
        // a 500, an aborted stream) would turn into a request loop. One attempt per granted lifetime; the
        // next successful search, reconnect repair or manual refresh re-arms it.
        const key = `${lifetime.expiresAt}:${lifetime.receivedAt}`
        if (firedForRef.current === key) return

        let timer: number | undefined
        const fire = () => {
            firedForRef.current = key
            renewRef.current()
        }
        const schedule = () => {
            document.removeEventListener('visibilitychange', onVisible)
            timer = window.setTimeout(fire, Math.max(0, lifetime.expiresAt - granted * RENEWAL_LEAD_FRACTION - Date.now()))
        }
        const onVisible = () => {
            if (!document.hidden) schedule()
        }
        if (document.hidden) document.addEventListener('visibilitychange', onVisible)
        else schedule()
        return () => {
            document.removeEventListener('visibilitychange', onVisible)
            if (timer !== undefined) window.clearTimeout(timer)
        }
    }, [lifetime, enabled])
}
